import React from 'react';
import {
  Loading,
  RecordContextProvider,
  SingleFieldList,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { Grid } from '@mui/material';

import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';
import ArrayDiffField from '../utils/auditTracks/ArrayDiffField';
import ChipStringField from '../../customFields/ChipStringField';
import { Member } from '../../utilities/schemas/member';

const MemberAuditDetails = () => {
  const record = useRecordContext<AuditTrack<Member>>();
  const { data, isLoading } = useGetOne<AuditTrack<Member>>(
    `audit-tracks/${record?.id}/previous`,
    { id: '' },
    { enabled: !!record },
  );

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator<Member>({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    firstName: resourceData.firstName?.hasChanged,
    lastName: resourceData.lastName?.hasChanged,
    email: resourceData.email?.hasChanged,
    active: resourceData.active?.hasChanged,
    permissionRoleIds: resourceData.permissionRoleIds?.hasChanged,
    teams: resourceData.teams?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.firstName && <TextDiffField source="firstName" label="First name" />}
          {hasFieldChanges.lastName && <TextDiffField source="lastName" label="Last name" />}
          {hasFieldChanges.email && <TextDiffField source="email" label="Email" />}
          {hasFieldChanges.active && <TextDiffField source="active" label="Active" />}

          {hasFieldChanges.permissionRoleIds && (
            <ArrayDiffField source="permissionRoleIds" label="Permission roles">
              <SingleFieldList linkType={false}>
                <ChipStringField />
              </SingleFieldList>
            </ArrayDiffField>
          )}

          {hasFieldChanges.teams && (
            <ArrayDiffField source="teams" label="Teams">
              <SingleFieldList linkType={false}>
                <ChipStringField />
              </SingleFieldList>
            </ArrayDiffField>
          )}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default MemberAuditDetails;
